import * as widgetActions from '../actions/widget.actions';

import { Action, createReducer, on } from '@ngrx/store';
import { NomObjectClass, WidgetType } from '../constants';
import { NomenclatureItem, Widget } from '../models/core.models';
import { WidgetPreset, WidgetVizType } from '../models/widget.models';

import { DataObjectRef } from '../services/object.service';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { augmentObjectWithIconAndImage } from 'src/app/shared/utils/object-utils';
import cloneDeep from 'lodash-es/cloneDeep';
import { feedUnloaded } from '../actions/feed.actions';
import { objectChanged } from '../actions/core.actions';
import { produce } from 'immer';
import { selectDashboardPage } from '../actions/object.actions';

export interface WidgetViewConfig {
  label: string;
  path: string;
  icon: IconProp;
  selected: boolean;
}

export interface WidgetPreviewConfig {
  widgetPresetId: number;
  vizTypeId: number;
}

const widgetConfigurations: WidgetViewConfig[] = [
  {
    label: 'widget-actions.button.attached-pivots',
    path: 'pivots',
    icon: ['fas', 'cube'],
    selected: true,
  },
  {
    label: 'widget-actions.button.attached-filters',
    path: 'filters',
    icon: ['fas', 'tag'],
    selected: true,
  },
  {
    label: 'widget-actions.button.Attached-data-sources',
    path: 'datasources',
    icon: ['fas', 'database'],
    selected: true,
  },
];

export const augmentWidgetFields = (widget: Widget): Widget => ({
  ...widget,
  datasources: widget.datasources?.map(augmentObjectWithIconAndImage) ?? [],
  dimensions_filter:
    widget.dimensions_filter?.map(augmentObjectWithIconAndImage) ?? [],
  dimensions_pivot:
    widget.dimensions_pivot?.map(augmentObjectWithIconAndImage) ?? [],
});

export interface WidgetState {
  widgets: Map<string, Widget>;
  widgetsAllPages: Map<string, Widget>; // pageId -> Widgets
  widgetPresets: Array<WidgetPreset>;
  widgetVizTypes: Map<number, WidgetVizType[]>; // chartId -> chartTypeId
  widgetPresetGroups: NomenclatureItem[];
  loadingWidgets: boolean;
  loadingWidgetPresets: boolean;
  widgetUpdatedSuccessfully: boolean;
  countSelectedWidgets: number;
  searchString: string;
  viewConfig: WidgetViewConfig[];
  currentObject: DataObjectRef;
  selectedPreviewWidgetConfig: WidgetPreviewConfig;
  selectedPlaceholderWidgetId: string;
  // * Per-page loading state management
  pageLoadingStates: Map<number, boolean>; // pageId -> loading state
  loadedPageIds: Set<number>; // Track which pages have been loaded
}

export const initialState: WidgetState = {
  widgets: new Map<string, Widget>(),
  widgetsAllPages: new Map<string, Widget>(),
  // * maybe we need to add widgetsAllPages loading state
  widgetVizTypes: new Map<number, WidgetVizType[]>(),
  widgetPresets: [],
  widgetPresetGroups: [],
  loadingWidgets: false,
  loadingWidgetPresets: false,
  widgetUpdatedSuccessfully: false,
  countSelectedWidgets: 0,
  searchString: '',
  viewConfig: [],
  currentObject: {
    objectId: '',
    objectClassId: null,
  },
  selectedPreviewWidgetConfig: null,
  selectedPlaceholderWidgetId: null,
  // !!! ??? Per-page loading state management - since we have mass load - maybe we do not need this - find out!!!
  pageLoadingStates: new Map<number, boolean>(),
  // !!! ??? - maybe we do not need this - find out!!!
  loadedPageIds: new Set<number>(),
};

const _widgetReducer = createReducer(
  initialState,

  on(widgetActions.loadWidgets, (state, { pageId }) => {
    const widgetCount = state.widgets.size;

    return {
      ...state,
      loadingWidgets: true,
      // * Set per-page loading state if pageId is provided
      pageLoadingStates: pageId
        ? new Map(state.pageLoadingStates).set(pageId, true)
        : state.pageLoadingStates,
      // * Only clear widgets if no pageId (for backward compatibility)
      widgets: pageId ? state.widgets : new Map<string, Widget>(),
    };
  }),

  on(objectChanged, (state, { object }) => {
    return {
      ...state,
      currentObject: {
        objectId: object?.id,
        objectClassId: object?.object_class_id,
      },
      // * Reset per-page loading states when switching objects
      pageLoadingStates: new Map<number, boolean>(),
      loadedPageIds: new Set<number>(),
      // * Clear widgets from previous dashboard
      widgets: new Map<string, Widget>(),
    };
  }),

  on(widgetActions.loadWidgetsSuccess, (state, { widgets, pageId }) => {
    const newWidgets = new Map(state.widgets);
    const widgetPageIds = new Set();
    let removedWidgets = [];

    // * Guard: If pageId provided, only replace widgets for that specific Page
    if (pageId) {
      // * remove existing Widgets for this specific Page
      for (const [widgetId, widget] of newWidgets.entries()) {
        if (widget.page_id === pageId) {
          removedWidgets.push({
            widgetId,
            page_id: widget.page_id,
            hasData: !!widget.data,
          });

          newWidgets.delete(widgetId);
        }
      }
    } else {
      // * Guard: If no pageId, clear all widgets (legacy behavior)
      newWidgets.clear();
    }

    // * Add the new widgets
    for (const widget of widgets) {
      const updatedWidget = augmentWidgetFields(widget);

      // * Preserve existing data field if widget already exists and has data
      const existingWidget = state.widgets.get(widget.id);

      if (existingWidget && existingWidget.data && !updatedWidget.data) {
        updatedWidget.data = existingWidget.data;
      }

      newWidgets.set(widget.id, updatedWidget);
      if (widget.page_id) widgetPageIds.add(widget.page_id);
    }

    return {
      ...state,
      loadingWidgets: false,
      widgets: newWidgets,
      viewConfig: widgetConfigurations,
      // * Update per-page loading states
      pageLoadingStates: pageId
        ? new Map(state.pageLoadingStates).set(pageId, false)
        : state.pageLoadingStates,
      // * Mark page as loaded if pageId is provided
      loadedPageIds: pageId
        ? new Set(state.loadedPageIds).add(pageId)
        : state.loadedPageIds,
    };
  }),

  // * Presentation mode widget loading handlers
  on(widgetActions.loadAllWidgetsForPresentation, (state) => {
    return {
      ...state,
      loadingWidgets: true,
      // * Reset page loading states since we're loading everything fresh
      pageLoadingStates: new Map<number, boolean>(),
    };
  }),

  on(
    widgetActions.loadAllWidgetsForPresentationSuccess,
    (state, { widgets, pageIds }) => {
      const newWidgets = new Map<string, Widget>();

      // * merge all Widgets into the store (excluding placeholders)
      for (const widget of widgets) {
        if (widget.widget_type_id === WidgetType.WIDGET_TYPE_PLACEHOLDER) {
          continue;
        }

        const updatedWidget = augmentWidgetFields(widget);
        newWidgets.set(widget.id, updatedWidget);
      }

      const newLoadedPageIds = new Set(state.loadedPageIds);
      pageIds.forEach((pageId) => newLoadedPageIds.add(pageId));

      return {
        ...state,
        loadingWidgets: false,
        viewConfig: widgetConfigurations,
        pageLoadingStates: new Map<number, boolean>(),
        loadedPageIds: newLoadedPageIds,
        widgetsAllPages: newWidgets,
      };
    },
  ),

  on(widgetActions.loadAllWidgetsForPresentationFail, (state, { error }) => {
    return {
      ...state,
      loadingWidgets: false,
    };
  }),

  on(widgetActions.clearAllWidgets, (state) => {
    return {
      ...state,
      widgets: new Map<string, Widget>(),
      loadedPageIds: new Set<number>(),
      pageLoadingStates: new Map<number, boolean>(),
    };
  }),

  // * Clear widgets when page selection changes to prevent accumulation
  on(selectDashboardPage, (state, { pageId }) => {
    const widgetCount = state.widgets.size;

    return {
      ...state,
      widgets: new Map<string, Widget>(),
      loadedPageIds: new Set<number>(),
      pageLoadingStates: new Map<number, boolean>(),
    };
  }),

  on(
    widgetActions.loadWidget,
    produce((draft, { widgetId }) => {
      if (draft.widgets.has(widgetId)) {
        const w = draft.widgets.get(widgetId)!; // safe after has()
        draft.widgets.set(widgetId, { ...w, loading: true, is_loaded: false });
        return;
      }

      if (draft.widgetsAllPages.has(widgetId)) {
        const w = draft.widgetsAllPages.get(widgetId)!; // safe after has()
        draft.widgetsAllPages.set(widgetId, {
          ...w,
          loading: true,
          is_loaded: false,
        });
        return;
      }

      // Optionally add a placeholder if not found anywhere:
      // draft.widgets.set(widgetId, { id: widgetId, loading: true } as Widget);
    }),
  ),

  on(
    widgetActions.loadWidgetSuccess,
    produce((draft, { widgetId, widgetData }) => {
      if (draft.widgets.has(widgetId)) {
        const prev = draft.widgets.get(widgetId)!;
        draft.widgets.set(widgetId, {
          ...prev,
          loading: false,
          data: widgetData,
          is_loaded: true,
        });
      } else if (draft.widgetsAllPages.has(widgetId)) {
        const prevAll = draft.widgetsAllPages.get(widgetId)!;
        draft.widgetsAllPages.set(widgetId, {
          ...prevAll,
          loading: false,
          data: widgetData,
        });
      }

      draft.widgetUpdatedSuccessfully = false;
    }),
  ),

  on(
    widgetActions.loadWidgetFail,
    produce((draft, { widgetId, error }) => {
      const widget = draft.widgets.get(widgetId);
      if (widget) {
        draft.widgets.set(widgetId, {
          ...widget,
          loading: false,
          is_loaded: true, // this indicates loading attempted
        });
      }
    }),
  ),

  on(
    widgetActions.startBatchLoadWidgets,
    produce((draft, { widgetIds }) => {
      widgetIds.forEach((widgetId) => {
        const widget = draft.widgetsAllPages.get(widgetId);
        if (widget) {
          draft.widgetsAllPages.set(widgetId, {
            ...widget,
            loading: true,
          });
        }
      });
    }),
  ),

  on(
    widgetActions.batchUpdateWidgetData,
    produce((draft, { updates }) => {
      updates.forEach(({ widgetId, data }) => {
        const widget = draft.widgetsAllPages.get(widgetId);
        if (widget) {
          draft.widgetsAllPages.set(widgetId, {
            ...widget,
            loading: false,
            data,
          });
        }
      });
    }),
  ),

  on(
    widgetActions.loadSingleWidget,
    produce((draft, { widgetId }) => {
      const widget = draft.widgets.get(widgetId);
      if (widget) {
        draft.widgets.set(widgetId, {
          ...widget,
          loading: true,
        });
      }
    }),
  ),

  on(
    widgetActions.loadSingleWidgetSuccess,
    produce((draft, { widget }) => {
      const existingWidget = draft.widgets.get(widget.id);
      if (existingWidget) {
        // * update the existing widget with fresh metadata while preserving current data
        const updatedWidget = augmentWidgetFields(widget);
        updatedWidget.loading = false;
        draft.widgets.set(widget.id, updatedWidget);
      }
    }),
  ),

  on(
    widgetActions.loadSingleWidgetFail,
    produce((draft, { widgetId, error }) => {
      const widget = draft.widgets.get(widgetId);
      if (widget) {
        draft.widgets.set(widgetId, {
          ...widget,
          loading: false,
        });
      }
    }),
  ),

  on(widgetActions.updateWidget, (state, { widgetId, data }) => {
    return {
      ...state,
      //widgetUpdatedSuccessfully: true,
    };
  }),

  on(
    widgetActions.updateWidgetSuccess,
    produce((draft, { widgetId, data: updatedWidgetData }) => {
      const existingWidget = draft.widgets.get(widgetId);
      if (!existingWidget) return;

      const updated = augmentWidgetFields(updatedWidgetData);
      updated.loading = false;

      draft.widgets.set(widgetId, updated);
      draft.widgetUpdatedSuccessfully = true;
    }),
  ),

  on(
    widgetActions.updateWidgetFail,
    produce((draft, { widgetId, error }) => {
      const widget = draft.widgets.get(widgetId);
      if (!widget) return;

      draft.widgets.set(widgetId, {
        ...widget,
        loading: false,
      });

      draft.widgetUpdatedSuccessfully = false;
    }),
  ),

  on(widgetActions.loadWidgetPresets, (state) => {
    return {
      ...state,
      loadingWidgetPresets: true,
    };
  }),

  on(widgetActions.loadWidgetPresetsSuccess, (state, { widgetPresets }) => {
    return {
      ...state,
      loadingWidgetPresets: false,
      widgetPresets: widgetPresets,
    };
  }),

  on(
    widgetActions.createWidgetSuccess,
    produce((draft, { widget }) => {
      const widgets = draft.widgets;

      // Only apply placeholder-shifting logic for FEED objects
      const isFeedContext =
        draft.currentObject?.objectClassId === NomObjectClass.FEED;

      let placeholderEntry: [string, Widget] | undefined;

      if (isFeedContext) {
        for (const entry of widgets.entries()) {
          if (entry[1]?.widget_type_id === WidgetType.WIDGET_TYPE_PLACEHOLDER) {
            placeholderEntry = entry;
            break;
          }
        }
      }

      if (!widgets.has(widget.id)) {
        const placeholderId = draft.selectedPlaceholderWidgetId;
        if (placeholderId && widgets.has(placeholderId)) {
          widgets.delete(placeholderId);
        }
      }

      widgets.set(widget.id, augmentWidgetFields(widget));

      // shift placeholder Y only in FEED context
      if (isFeedContext && placeholderEntry) {
        const [phId, phWidget] = placeholderEntry;

        if (widgets.has(phId)) {
          widgets.set(phId, {
            ...phWidget,
            y: (phWidget.y ?? 0) + (widget.rows ?? 0),
          });
        }
      }

      draft.selectedPlaceholderWidgetId = null;
    }),
  ),

  on(
    widgetActions.deleteWidgetSuccess,
    produce((draft, { widgetId }) => {
      draft.widgets.delete(widgetId);
    }),
  ),

  on(
    widgetActions.deleteWidgetsByPageId,
    produce((draft, { pageId }) => {
      // ! remove all Widgets with matching page_id
      for (const [widgetId, widget] of draft.widgets.entries()) {
        if (widget.page_id === pageId) {
          draft.widgets.delete(widgetId);
        }
      }

      draft.pageLoadingStates.delete(pageId);
      draft.loadedPageIds.delete(pageId);
    }),
  ),

  on(widgetActions.widgetResized, (state, { widgetId, x, y, cols, rows }) => {
    const widget = state.widgets.get(widgetId);
    if (!widget) return state;

    const newWidgets = new Map(state.widgets);
    newWidgets.set(widgetId, {
      ...widget,
      x,
      y,
      cols,
      rows,
      sizeX: cols,
      sizeY: rows,
      row: y,
      col: x,
    });

    return {
      ...state,
      widgets: newWidgets,
    };
  }),

  on(widgetActions.widgetsResizedBatch, (state, { widgets }) => {
    const newWidgets = new Map(state.widgets);

    for (const { widgetId, x, y, cols, rows } of widgets) {
      const widget = newWidgets.get(widgetId);
      if (!widget) continue;

      newWidgets.set(widgetId, {
        ...widget,
        x,
        y,
        cols,
        rows,
        sizeX: cols,
        sizeY: rows,
        row: y,
        col: x,
      });
    }

    return {
      ...state,
      widgets: newWidgets,
    };
  }),

  on(widgetActions.widgetsRepositioned, (state, { widgets }) => {
    const newWidgets = new Map(state.widgets);

    widgets.forEach(({ id, x, y, w, h }) => {
      const widget = newWidgets.get(id);
      if (widget) {
        newWidgets.set(id, {
          ...widget,
          x,
          y,
          cols: w,
          rows: h,
          sizeX: w,
          sizeY: h,
          row: y,
          col: x,
        });
      }
    });

    return {
      ...state,
      widgets: newWidgets,
    };
  }),

  on(
    widgetActions.widgetVisibilityChanged,
    produce((draft, { widgetId, isVisible }) => {
      const widget = draft.widgets.get(widgetId);
      if (!widget) return;

      draft.widgets.set(widgetId, {
        ...widget,
        is_visible: isVisible,
      });
    }),
  ),

  on(
    widgetActions.loadVizualizationTypesSuccess,
    (state, { widgetVizTypes, chartId }) => {
      return {
        ...state,
        widgetVizTypes: new Map().set(chartId, widgetVizTypes),
      };
    },
  ),

  on(
    widgetActions.widgetUpdateChartOptions,
    produce((draft, { widgetId, chartOptions }) => {
      const widget = draft.widgets.get(widgetId);
      if (!widget) return;

      draft.widgets.set(widgetId, {
        ...widget,
        chart_options: chartOptions,
      });
    }),
  ),

  on(
    widgetActions.loadWidgetPresetGroupsSuccess,
    (state, { widgetPresetGroups }) => {
      return {
        ...state,
        widgetPresetGroups: widgetPresetGroups,
      };
    },
  ),

  on(feedUnloaded, (state) => {
    return initialState;
  }),

  on(widgetActions.deleteWidgets, (state) => {
    return {
      ...state,
      loadingWidgets: true,
    };
  }),

  on(
    widgetActions.deleteWidgetsSuccess,
    produce((draft, { widgetIds }) => {
      for (const widgetId of widgetIds) {
        draft.widgets.delete(widgetId);
      }
      draft.loadingWidgets = false;
    }),
  ),

  on(widgetActions.deleteWidgetsFail, (state) => {
    return {
      ...state,
      loadingWidgets: false,
    };
  }),

  // * Handle mass widget operation success - remove moved widgets from current view
  on(
    widgetActions.widgetMassOperationSuccess,
    produce((draft, { operations }) => {
      // * remove ONLY widgets that were MOVED (not copied) from current view
      const movedWidgetIds = operations
        .filter((op) => op.action === 'move')
        .map((op) => op.widget_id);

      for (const widgetId of movedWidgetIds) {
        draft.widgets.delete(widgetId);
      }

      draft.loadingWidgets = false;
    }),
  ),

  on(widgetActions.widgetMassOperationFail, (state) => {
    return {
      ...state,
      loadingWidgets: false,
    };
  }),

  on(widgetActions.searchWidgets, (state, { query }) => {
    return {
      ...state,
      searchString: query,
    };
  }),

  on(
    widgetActions.toggleWidgetViewOption,
    produce((draft, { optionName }) => {
      const option = draft.viewConfig.find((opt) => opt.label === optionName);
      if (option) {
        option.selected = !option.selected;
      }
    }),
  ),

  on(widgetActions.selectWidgetPlaceholderId, (state, { id }) => {
    return {
      ...state,
      selectedPlaceholderWidgetId: id,
    };
  }),

  on(
    widgetActions.applyWidgetDatasourceIds,
    produce((draft, { widgetId, datasourceIds, datasourceClassIds }) => {
      const widget = draft.widgets.get(widgetId);

      if (!widget) return;

      const updated = augmentWidgetFields(widget);

      updated.settings.datasource_object_id = datasourceIds;
      updated.settings.datasource_object_class_id = datasourceClassIds;

      draft.widgets.set(widgetId, updated);
    }),
  ),

  on(
    widgetActions.applyWidgetFilterIds,
    produce((draft, { widgetId, filterIds }) => {
      const widget = draft.widgets.get(widgetId);

      if (!widget) return;

      const updated = augmentWidgetFields(widget);
      updated.settings.dimension_id_filter = filterIds;

      draft.widgets.set(widgetId, updated);
    }),
  ),

  on(
    widgetActions.applyAllWidgetsFilterIds,
    produce((draft, { widgetIds, filterIds }) => {
      for (const widgetId of widgetIds) {
        const widget = draft.widgets.get(widgetId);
        if (!widget) continue;

        widget.settings.dimension_id_filter = filterIds;
      }
    }),
  ),

  on(
    widgetActions.applyAllWidgetsPivotIds,
    produce((draft, { widgetIds, pivotIds }) => {
      for (const widgetId of widgetIds) {
        const widget = draft.widgets.get(widgetId);
        if (!widget) continue;

        widget.settings.dimension_id_pivot = pivotIds;
      }
    }),
  ),

  on(
    widgetActions.applyWidgetPivotIds,
    produce((draft, { widgetId, pivotIds }) => {
      const widget = draft.widgets.get(widgetId);
      if (!widget) return;

      const updated = augmentWidgetFields(widget);
      updated.settings.dimension_id_pivot = pivotIds;

      draft.widgets.set(widgetId, updated);
    }),
  ),

  on(
    widgetActions.removeAllWidgetsFilters,
    produce((draft, { widgetIds }) => {
      for (const widgetId of widgetIds) {
        const widget = draft.widgets.get(widgetId);
        if (!widget) continue;

        widget.settings.dimension_id_filter = [];
      }
    }),
  ),

  on(
    widgetActions.removeAllWidgetsPivots,
    produce((draft, { widgetIds }) => {
      for (const widgetId of widgetIds) {
        const widget = draft.widgets.get(widgetId);
        if (!widget) continue;

        widget.settings.dimension_id_pivot = [];
      }
    }),
  ),

  on(
    widgetActions.removeWidgetFilter,
    produce((draft, { widgetId, filterId }) => {
      const widget = draft.widgets.get(widgetId);
      if (!widget) return;

      widget.settings.dimension_id_filter =
        widget.settings.dimension_id_filter.filter((id) => id !== filterId);
    }),
  ),

  on(
    widgetActions.removeWidgetPivot,
    produce((draft, { widgetId, pivotId }) => {
      const widget = draft.widgets.get(widgetId);
      if (!widget) return;

      widget.settings.dimension_id_pivot =
        widget.settings.dimension_id_pivot.filter((id) => id !== pivotId);
    }),
  ),

  on(
    widgetActions.updateWidgetState,
    produce((draft, { widgetId, widget }) => {
      const existing = draft.widgets.get(widgetId);
      if (!existing) return;

      draft.widgets.set(widgetId, {
        ...existing,
        ...widget,
      });
    }),
  ),

  // * Swap Widget Type - replace old widget with new one in the store
  on(
    widgetActions.swapWidgetTypeSuccess,
    produce((draft, { oldWidgetId, widget }) => {
      const existingWidget = draft.widgets.get(oldWidgetId);
      if (!existingWidget) return;

      // Replace old widget in-place, preserving the map key so Angular's
      // trackBy (which uses widget.id) sees this as an update, not a remove+add.
      const updatedWidget = augmentWidgetFields(widget);
      updatedWidget.id = oldWidgetId;
      updatedWidget.x = existingWidget.x;
      updatedWidget.y = existingWidget.y;
      updatedWidget.col = existingWidget.col;
      updatedWidget.row = existingWidget.row;
      updatedWidget.cols = existingWidget.cols;
      updatedWidget.rows = existingWidget.rows;
      updatedWidget.sizeX = existingWidget.sizeX;
      updatedWidget.sizeY = existingWidget.sizeY;
      updatedWidget.loading = true;

      draft.widgets.set(oldWidgetId, updatedWidget);
    }),
  ),

  on(
    widgetActions.swapWidgetTypeFail,
    produce((draft, { widgetId }) => {
      const widget = draft.widgets.get(widgetId);
      if (widget) {
        draft.widgets.set(widgetId, { ...widget, loading: false });
      }
    }),
  ),

  on(
    widgetActions.massUpdateWidgetsSuccess,
    produce((draft, { widgetIds, data, countUpdated, widgets }) => {
      // * Update each affected widget with the new data returned from the mass update API call
      for (const widgetId of widgetIds) {
        const existingWidget = draft.widgets.get(widgetId);
        if (!existingWidget) continue;

        const updatedWidget = widgets?.find((w) => w.id === widgetId);
        if (!updatedWidget) continue;

        draft.widgets.set(widgetId, {
          ...existingWidget,
          ...augmentWidgetFields(updatedWidget),
          loading: false,
        });
      }

      draft.widgetUpdatedSuccessfully = true;
    }),
  ),
);

export function widgetReducer(state: WidgetState, action: Action) {
  return _widgetReducer(state, action);
}
